import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API } from '@/constants/bluemonk';

export const AdminInputRequestModal = ({ open, onOpenChange, item, headers, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    whyNeeded: '',
    description: '',
    brand: 'all',
    formatRequired: '',
    deadline: '',
    priority: 'P1',
    blocksDeliverables: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        whyNeeded: item.whyNeeded || '',
        description: item.description || '',
        brand: item.brand || 'all',
        formatRequired: item.formatRequired || '',
        deadline: item.deadline || '',
        priority: item.priority || 'P1',
        blocksDeliverables: item.blocksDeliverables || [],
      });
    } else {
      setFormData({
        title: '',
        whyNeeded: '',
        description: '',
        brand: 'all',
        formatRequired: '',
        deadline: '',
        priority: 'P1',
        blocksDeliverables: [],
      });
    }
  }, [item, open]);

  const handleSubmit = async () => {
    if (!formData.title || !formData.whyNeeded || !formData.deadline) {
      toast.error('Please fill required fields');
      return;
    }

    setLoading(true);
    try {
      if (item) {
        await axios.put(`${API}/input-requests/${item.id}`, formData, { headers });
        toast.success('Input request updated');
      } else {
        await axios.post(`${API}/input-requests`, formData, { headers });
        toast.success('Input request created');
      }
      onSuccess();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit' : 'Add'} Input Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Why Needed *</Label>
            <Textarea
              value={formData.whyNeeded}
              onChange={(e) => setFormData({ ...formData, whyNeeded: e.target.value })}
              className="mt-1"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Brand</Label>
              <Select value={formData.brand} onValueChange={(v) => setFormData({ ...formData, brand: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  <SelectItem value="renew-circuits">Renew Circuits</SelectItem>
                  <SelectItem value="bluemonk-group">BlueMonk Group</SelectItem>
                  <SelectItem value="deshwal">Deshwal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P0">P0 (Critical)</SelectItem>
                  <SelectItem value="P1">P1 (Important)</SelectItem>
                  <SelectItem value="P2">P2 (Nice to have)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Format Required</Label>
            <Input
              value={formData.formatRequired}
              onChange={(e) => setFormData({ ...formData, formatRequired: e.target.value })}
              className="mt-1"
              placeholder="e.g., PDF or Excel"
            />
          </div>

          <div>
            <Label>Deadline *</Label>
            <Input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Blocks Deliverables (comma-separated)</Label>
            <Input
              value={formData.blocksDeliverables.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                blocksDeliverables: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              })}
              className="mt-1"
              placeholder="Website, Ads, Social"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-primary hover:bg-primary/90">
              {loading ? 'Saving...' : item ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
